import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Spin, Result, Button, Typography, Descriptions, Space, message } from 'antd';
import { CheckCircleOutlined, CreditCardOutlined, LoadingOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;

interface SubscriptionData {
  id: number;
  status: string;
  payment_status: string;
  price: number;
  start_date: string;
  end_date: string;
  customer: {
    id: number;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
  };
  offer: {
    id: number;
    title: string;
    description: string;
    price: number;
    duration: number;
  };
}

const SubscriptionPaymentPage: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/subscriptions/${subscriptionId}`);
        if (response.data.success) {
          setSubscription(response.data.data);
          // Si déjà payé, montrer le succès
          if (response.data.data.payment_status === 'paid') {
            setPaymentSuccess(true);
          }
        } else {
          setError('Abonnement non trouvé');
        }
      } catch (err: any) {
        console.error('Erreur:', err);
        setError(err.response?.data?.message || 'Impossible de charger les informations');
      } finally {
        setLoading(false);
      }
    };

    if (subscriptionId) {
      fetchSubscription();
    }
  }, [subscriptionId]);

  const handlePayment = async () => {
    if (!subscription) return;
    
    setProcessing(true);
    try {
      // Initier le paiement via FineoPay ou autre méthode
      const response = await api.post('/payments/subscription/initiate', {
        subscriptionId: subscription.id,
        amount: subscription.price || subscription.offer?.price,
        description: `Abonnement ${subscription.offer?.title}`
      });

      if (response.data.success) {
        // Si un lien de paiement externe est fourni
        if (response.data.paymentUrl) {
          window.location.href = response.data.paymentUrl;
        } else {
          // Simuler un paiement réussi pour la démo
          message.success('Paiement effectué avec succès !');
          setPaymentSuccess(true);
        }
      } else {
        message.error(response.data.message || 'Erreur lors du paiement');
      }
    } catch (err: any) {
      console.error('Erreur paiement:', err);
      message.error(err.response?.data?.message || 'Erreur lors du paiement');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a543d 0%, #1a8c6a 100%)'
      }}>
        <Card style={{ width: 400, textAlign: 'center' }}>
          <Spin size="large" />
          <Title level={4} style={{ marginTop: 16 }}>Chargement...</Title>
        </Card>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a543d 0%, #1a8c6a 100%)'
      }}>
        <Card style={{ width: 500 }}>
          <Result
            status="error"
            title="Erreur"
            subTitle={error || "Abonnement introuvable"}
            extra={[
              <Button type="primary" key="home" onClick={() => window.close()}>
                Fermer
              </Button>
            ]}
          />
        </Card>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a543d 0%, #1a8c6a 100%)'
      }}>
        <Card style={{ width: 500 }}>
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Paiement effectué !"
            subTitle={`Votre abonnement "${subscription.offer?.title}" est maintenant actif.`}
            extra={[
              <Button type="primary" key="close" onClick={() => window.close()}>
                Fermer
              </Button>
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      padding: 20,
      background: 'linear-gradient(135deg, #0a543d 0%, #1a8c6a 100%)'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 600,
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <CreditCardOutlined style={{ fontSize: 48, color: '#0a543d' }} />
          <Title level={2} style={{ margin: '16px 0 8px', color: '#0a543d' }}>
            Paiement de l'abonnement
          </Title>
          <Text type="secondary">
            Finalisez votre souscription en effectuant le paiement
          </Text>
        </div>

        <Card 
          type="inner" 
          title="Détails de l'offre"
          style={{ marginBottom: 24, background: '#f6ffed', borderColor: '#b7eb8f' }}
        >
          <Descriptions column={1}>
            <Descriptions.Item label="Offre">
              <Text strong>{subscription.offer?.title}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {subscription.offer?.description}
            </Descriptions.Item>
            <Descriptions.Item label="Durée">
              {subscription.offer?.duration} mois
            </Descriptions.Item>
            <Descriptions.Item label="Montant">
              <Text strong style={{ fontSize: 20, color: '#0a543d' }}>
                {(subscription.price || subscription.offer?.price)?.toLocaleString()} F CFA
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card type="inner" title="Vos informations" style={{ marginBottom: 24 }}>
          <Descriptions column={1}>
            <Descriptions.Item label="Nom">
              {subscription.customer?.first_name} {subscription.customer?.last_name}
            </Descriptions.Item>
            {subscription.customer?.email && (
              <Descriptions.Item label="Email">
                {subscription.customer.email}
              </Descriptions.Item>
            )}
            {subscription.customer?.phone && (
              <Descriptions.Item label="Téléphone">
                {subscription.customer.phone}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        <Space direction="vertical" style={{ width: '100%' }}>
          <Button 
            type="primary" 
            size="large" 
            block
            icon={processing ? <LoadingOutlined /> : <CreditCardOutlined />}
            onClick={handlePayment}
            loading={processing}
            style={{ 
              height: 56, 
              fontSize: 18,
              borderRadius: 12,
              background: '#0a543d'
            }}
          >
            {processing ? 'Traitement en cours...' : `Payer ${(subscription.price || subscription.offer?.price)?.toLocaleString()} F CFA`}
          </Button>
          
          <Text type="secondary" style={{ textAlign: 'center', display: 'block', marginTop: 12 }}>
            Paiement sécurisé via FineoPay
          </Text>
        </Space>
      </Card>
    </div>
  );
};

export default SubscriptionPaymentPage;
